import React, { useEffect, useState } from 'react'
import { 
  TrendingUp, AlertCircle, Gift, Calendar, 
  ShoppingCart, Info, X, ChevronRight
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import { format, differenceInDays, addDays, parseISO } from 'date-fns'
import { isOverdue } from '@/utils/dateUtils'

interface Insight {
  id: string
  type: 'warning' | 'info' | 'success' | 'tip'
  title: string
  message: string
  action?: {
    label: string
    onClick: () => void
  }
  icon: React.ReactNode
}

interface SmartInsightsPanelProps {
  children: any[]
  assignments: any[]
  onNavigate: (view: string) => void
}

export const SmartInsightsPanel: React.FC<SmartInsightsPanelProps> = ({
  children,
  assignments,
  onNavigate
}) => {
  const [insights, setInsights] = useState<Insight[]>([])
  const [dismissedInsights, setDismissedInsights] = useState<Set<string>>(() => {
    // Load dismissed insights from localStorage on mount
    const stored = localStorage.getItem('dismissedInsights')
    return stored ? new Set(JSON.parse(stored)) : new Set()
  })
  const [shoppingListCount, setShoppingListCount] = useState(0)

  useEffect(() => {
    generateInsights()
    loadShoppingList()
  }, [children, assignments])

  const loadShoppingList = async () => {
    try {
      const response = await apiClient.get('/api/meals/shopping-list')
      // Check if response has items array
      const items = response.items || response || []
      setShoppingListCount(Array.isArray(items) ? items.filter((item: any) => !item.purchased).length : 0)
    } catch (error) {
      console.error('Failed to load shopping list')
    }
  }

  const generateInsights = () => {
    const newInsights: Insight[] = []
    const today = new Date()

    // Check for inactive kids
    children.forEach(child => {
      const recentAssignments = assignments.filter(a => 
        a.assigned_to === child.id && 
        a.completed_at && 
        differenceInDays(today, parseISO(a.completed_at)) <= 7
      )
      
      if (recentAssignments.length === 0) {
        const lastCompleted = assignments
          .filter(a => a.assigned_to === child.id && a.completed_at)
          .sort((a, b) => new Date(b.completed_at!).getTime() - new Date(a.completed_at!).getTime())[0]
        
        const daysSinceLastChore = lastCompleted 
          ? differenceInDays(today, parseISO(lastCompleted.completed_at!))
          : null

        if (daysSinceLastChore && daysSinceLastChore >= 3) {
          newInsights.push({
            id: `inactive-${child.id}`,
            type: 'warning',
            title: 'Inactive Kid Alert',
            message: `${child.display_name} hasn't completed chores for ${daysSinceLastChore} days`,
            action: {
              label: 'Assign Chore',
              onClick: () => onNavigate('assign')
            },
            icon: <AlertCircle className="w-5 h-5" />
          })
        }
      }
    })

    // Check for upcoming birthdays
    children.forEach(child => {
      // This would need birthday data in the child object
      // Check for actual birthdays from user data
      const tomorrow = addDays(today, 1)
      const nextWeek = addDays(today, 7)
      
      // Check if user has a birthdate and if it's coming up
      if (child.birthdate) {
        const birthdate = new Date(child.birthdate + 'T12:00:00')
        const birthdayThisYear = new Date(today.getFullYear(), birthdate.getMonth(), birthdate.getDate())
        const daysDiff = Math.floor((birthdayThisYear.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
        
        if (daysDiff === 1) {
          newInsights.push({
            id: `birthday-${child.id}`,
            type: 'info',
            title: 'Birthday Tomorrow!',
            message: `${child.display_name}'s birthday is tomorrow - consider bonus points?`,
            action: {
              label: 'Award Bonus',
              onClick: () => onNavigate('rewards')
            },
            icon: <Gift className="w-5 h-5" />
          })
        } else if (daysDiff === 0) {
          newInsights.push({
            id: `birthday-${child.id}`,
            type: 'info',
            title: 'Happy Birthday!',
            message: `It's ${child.display_name}'s birthday today!`,
            action: {
              label: 'Award Bonus',
              onClick: () => onNavigate('rewards')
            },
            icon: <Gift className="w-5 h-5" />
          })
        }
      }
    })

    // Check overdue chores (using proper 6pm cutoff for today)
    const overdueCount = assignments.filter(a => 
      a.status === 'assigned' && 
      a.due_date && 
      isOverdue(a.due_date)
    ).length

    if (overdueCount >= 5) {
      newInsights.push({
        id: 'overdue-chores',
        type: 'warning',
        title: 'Many Overdue Chores',
        message: `${overdueCount} chores are overdue this week`,
        action: {
          label: 'Review',
          onClick: () => onNavigate('assignments')
        },
        icon: <AlertCircle className="w-5 h-5" />
      })
    }

    // Shopping list reminder
    if (shoppingListCount >= 10) {
      newInsights.push({
        id: 'shopping-list',
        type: 'info',
        title: 'Shopping List Ready',
        message: `Grocery list has ${shoppingListCount} items ready`,
        action: {
          label: 'View List',
          onClick: () => onNavigate('shopping')
        },
        icon: <ShoppingCart className="w-5 h-5" />
      })
    }

    // Weekly performance
    const weeklyCompleted = assignments.filter(a => 
      a.completed_at && 
      differenceInDays(today, parseISO(a.completed_at)) <= 7
    ).length

    const weeklyAssigned = assignments.filter(a => 
      a.created_at && 
      differenceInDays(today, parseISO(a.created_at)) <= 7
    ).length

    if (weeklyAssigned > 0) {
      const completionRate = Math.round((weeklyCompleted / weeklyAssigned) * 100)
      
      if (completionRate >= 80) {
        newInsights.push({
          id: 'great-week',
          type: 'success',
          title: 'Great Week!',
          message: `${completionRate}% completion rate this week - family is doing great!`,
          icon: <TrendingUp className="w-5 h-5" />
        })
      } else if (completionRate < 50) {
        newInsights.push({
          id: 'low-completion',
          type: 'warning',
          title: 'Low Completion Rate',
          message: `Only ${completionRate}% of chores completed this week`,
          action: {
            label: 'Send Reminders',
            onClick: () => onNavigate('notifications')
          },
          icon: <AlertCircle className="w-5 h-5" />
        })
      }
    }

    // Rotation reminder
    const lastRotation = localStorage.getItem('last_rotation_run')
    if (lastRotation) {
      const daysSinceRotation = differenceInDays(today, new Date(lastRotation.includes?.('T') ? lastRotation : lastRotation + 'T12:00:00'))
      if (daysSinceRotation >= 7) {
        newInsights.push({
          id: 'rotation-due',
          type: 'info',
          title: 'Rotation Due',
          message: 'Weekly rotation assignments are ready to run',
          action: {
            label: 'Run Rotations',
            onClick: () => onNavigate('rotations')
          },
          icon: <Calendar className="w-5 h-5" />
        })
      }
    }

    setInsights(newInsights.filter(i => !dismissedInsights.has(i.id)))
  }

  const dismissInsight = (id: string) => {
    console.log('Dismissing insight:', id)
    const newDismissed = new Set([...dismissedInsights, id])
    setDismissedInsights(newDismissed)
    // Store dismissed insights in localStorage so they persist
    localStorage.setItem('dismissedInsights', JSON.stringify(Array.from(newDismissed)))
    // Also filter out the dismissed insight immediately
    setInsights(prev => prev.filter(i => i.id !== id))
  }

  const getTypeStyles = (type: string) => {
    const styles = {
      warning: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-800',
        icon: 'text-yellow-600'
      },
      info: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-800',
        icon: 'text-blue-600'
      },
      success: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-800',
        icon: 'text-green-600'
      },
      tip: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        text: 'text-purple-800',
        icon: 'text-purple-600'
      }
    }
    return styles[type as keyof typeof styles] || styles.info
  }

  if (insights.length === 0) {
    return null
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4">
      <div className="flex items-center mb-3">
        <Info className="w-5 h-5 text-blue-600 mr-2" />
        <h3 className="font-semibold text-gray-900">Smart Insights</h3>
        <span className="ml-2 text-xs text-gray-500">
          {insights.length} {insights.length === 1 ? 'insight' : 'insights'}
        </span>
      </div>
      
      <div className="space-y-2">
        {insights.map(insight => {
          const styles = getTypeStyles(insight.type)
          
          return (
            <div
              key={insight.id}
              className={`
                relative flex items-start p-3 rounded-lg border
                ${styles.bg} ${styles.border}
              `}
            >
              <div className={`${styles.icon} mr-3 mt-0.5`}>
                {insight.icon}
              </div>
              
              <div className="flex-1 min-w-0">
                <h4 className={`text-sm font-medium ${styles.text}`}>
                  {insight.title}
                </h4>
                <p className={`text-sm ${styles.text} opacity-90 mt-0.5`}>
                  {insight.message}
                </p>
                
                {insight.action && (
                  <button
                    onClick={insight.action.onClick}
                    className={`
                      inline-flex items-center mt-2 text-sm font-medium
                      ${styles.text} hover:underline
                    `}
                  >
                    {insight.action.label}
                    <ChevronRight className="w-3 h-3 ml-1" />
                  </button>
                )}
              </div>
              
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  dismissInsight(insight.id)
                }}
                className={`
                  ml-2 p-1 rounded hover:bg-white/50 transition-colors
                  ${styles.text} opacity-60 hover:opacity-100
                `}
                title="Dismiss"
                type="button"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}